import React, { useRef, useState } from 'react';
import { X } from 'lucide-react';
import {
  ACCEPTED_DOCUMENT_TYPES,
  ACCEPTED_IMAGE_TYPES,
  MAX_DOCUMENTS,
  formatFileSize,
  validateFile,
} from '../../../utils/admissionValidation';

// File pickers for the admission form. Files are held in state and posted with
// the application — nothing is uploaded until the parent submits.

/** Student photo: one image, previewed, replaceable and removable. */
export const ProfileImagePicker = ({ file, previewUrl, onSelect, onRemove }) => {
  const inputRef = useRef(null);
  const [error, setError] = useState(null);

  const handleChange = (event) => {
    const chosen = event.target.files && event.target.files[0];
    event.target.value = '';
    if (!chosen) return;

    const message = validateFile(chosen, { isImage: true });
    if (message) {
      setError(message);
      return;
    }
    setError(null);
    onSelect(chosen);
  };

  return (
    <div className="ad-photo">
      <div className="ad-photo-frame">
        {previewUrl ? (
          <img src={previewUrl} alt="The student you selected" className="ad-photo-image" />
        ) : (
          <span className="ad-photo-empty" aria-hidden="true">
            No photo
          </span>
        )}
      </div>

      <div className="ad-photo-copy">
        <p className="ad-photo-title">Student photo</p>
        <p className="ad-photo-note">JPG or PNG, up to 5 MB. Optional.</p>

        <div className="ad-photo-actions">
          <button
            type="button"
            className="sp-btn sp-btn-outline ad-btn-small"
            onClick={() => inputRef.current && inputRef.current.click()}
          >
            {file ? 'Replace photo' : 'Choose photo'}
          </button>
          {file && (
            <button type="button" className="ad-text-button" onClick={onRemove}>
              Remove
            </button>
          )}
        </div>

        {file && <p className="ad-file-meta">{`${file.name} · ${formatFileSize(file.size)}`}</p>}
        {error && (
          <p className="ad-field-error" role="alert">
            {error}
          </p>
        )}
      </div>

      <input
        ref={inputRef}
        type="file"
        className="sp-visually-hidden"
        accept={ACCEPTED_IMAGE_TYPES.join(',')}
        onChange={handleChange}
        aria-label="Choose a student photo"
      />
    </div>
  );
};

/** Supporting documents: several files, listed with a remove control each. */
export const DocumentPicker = ({ documents, onAdd, onRemove }) => {
  const inputRef = useRef(null);
  const [error, setError] = useState(null);

  const handleChange = (event) => {
    const chosen = Array.from(event.target.files || []);
    event.target.value = '';
    if (chosen.length === 0) return;

    const accepted = [];
    let message = null;

    chosen.forEach((file) => {
      if (message) return;
      if (documents.length + accepted.length >= MAX_DOCUMENTS) {
        message = `You can attach up to ${MAX_DOCUMENTS} documents.`;
        return;
      }
      const fileError = validateFile(file, { existing: [...documents, ...accepted] });
      if (fileError) {
        message = fileError;
        return;
      }
      accepted.push({ id: `${file.name}-${file.size}-${file.lastModified}`, file });
    });

    setError(message);
    if (accepted.length > 0) onAdd(accepted);
  };

  return (
    <div className="ad-documents">
      <div className="ad-documents-head">
        <div>
          <p className="ad-photo-title">Supporting documents</p>
          <p className="ad-photo-note">
            Birth certificate, previous school report or anything else the school asks for. JPG, PNG
            or PDF, up to 5 MB each. Optional.
          </p>
        </div>
        <button
          type="button"
          className="sp-btn sp-btn-outline ad-btn-small"
          onClick={() => inputRef.current && inputRef.current.click()}
          disabled={documents.length >= MAX_DOCUMENTS}
        >
          Add files
        </button>
      </div>

      {documents.length > 0 && (
        <ul className="ad-file-list">
          {documents.map(({ id, file }) => (
            <li className="ad-file-row" key={id}>
              <span className="ad-file-kind" aria-hidden="true">
                {file.type === 'application/pdf' ? 'PDF' : 'IMG'}
              </span>
              <span className="ad-file-name">{file.name}</span>
              <span className="ad-file-size">{formatFileSize(file.size)}</span>
              <button
                type="button"
                className="ad-file-remove"
                onClick={() => onRemove(id)}
                aria-label={`Remove ${file.name}`}
              >
                <X size={16} strokeWidth={1.75} aria-hidden="true" />
              </button>
            </li>
          ))}
        </ul>
      )}

      <p className="ad-file-count" aria-live="polite">
        {documents.length} of {MAX_DOCUMENTS} files attached
      </p>

      {error && (
        <p className="ad-field-error" role="alert">
          {error}
        </p>
      )}

      <input
        ref={inputRef}
        type="file"
        multiple
        className="sp-visually-hidden"
        accept={ACCEPTED_DOCUMENT_TYPES.join(',')}
        onChange={handleChange}
        aria-label="Choose supporting documents"
      />
    </div>
  );
};
